import { InfiniteData, QueryClient } from '@tanstack/react-query'
import {
  DiscussionPage,
  discussionQueryKey,
  getCommentRow,
} from '../../api/comments'
import { CommentRow, CommentsSurface } from '../../generated/api/comment-row'

type Discussion = InfiniteData<CommentsSurface, DiscussionPage>
type CommentUpdate = (comment: CommentRow) => CommentRow

export const applyToComments = (
  comments: CommentRow[],
  commentId: string,
  update: CommentUpdate
): CommentRow[] =>
  comments.some((comment) => comment.id === commentId)
    ? comments.map((comment) =>
        comment.id === commentId ? update(comment) : comment
      )
    : comments

export const dropComment = (
  comments: CommentRow[],
  commentId: string
): CommentRow[] =>
  comments.some((comment) => comment.id === commentId)
    ? comments.filter((comment) => comment.id !== commentId)
    : comments

export const likedComment = (
  comment: CommentRow,
  liked: boolean
): CommentRow =>
  comment.likes.viewerLiked === liked
    ? comment
    : {
        ...comment,
        likes: {
          viewerLiked: liked,
          count: Math.max(0, comment.likes.count + (liked ? 1 : -1)),
        },
        actions: {
          ...comment.actions,
          canOpenLikers: liked || comment.likes.count > 1,
        },
      }

export const mapDiscussion = (
  data: Discussion | undefined,
  mapItems: (items: CommentRow[]) => CommentRow[]
): Discussion | undefined => {
  if (data == null) {
    return data
  }
  const pages = data.pages.map((page) => {
    const items = mapItems(page.items)
    return items === page.items ? page : { ...page, items }
  })
  return pages.every((page, index) => page === data.pages[index])
    ? data
    : { ...data, pages }
}

export const updateComment = (
  client: QueryClient,
  postId: string,
  commentId: string,
  update: CommentUpdate
): void => {
  client.setQueriesData<Discussion>(
    { queryKey: discussionQueryKey(postId) },
    (data) =>
      mapDiscussion(data, (items) => applyToComments(items, commentId, update))
  )
}

export const removeComment = (
  client: QueryClient,
  postId: string,
  commentId: string
): void => {
  client.setQueriesData<Discussion>(
    { queryKey: discussionQueryKey(postId) },
    (data) => mapDiscussion(data, (items) => dropComment(items, commentId))
  )
}

export const refreshComment = async (
  client: QueryClient,
  postId: string,
  commentId: string
): Promise<void> => {
  const { item } = (await getCommentRow(commentId)).data.data
  updateComment(client, postId, commentId, () => item)
}
